import React from 'react';
import { Link } from 'react-router-dom';
import './landingPageStyles.css';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.nikethana.safeher';
const APP_STORE_URL  = 'https://apps.apple.com/app/safeher/id6766123988';

const FEATURES = [
  {
    icon: '',
    title: 'Fully Anonymous',
    desc: 'Report incidents without revealing your identity. Your name is never stored, not even on our servers. SafeHer is always safe to use.',
  },
  {
    icon: '',
    title: 'Privacy by Design',
    desc: 'We collect only what\'s needed to keep you safe. Your data is never sold, never shared with advertisers, and never used against you.',
  },
  {
    icon: '',
    title: 'Moderator Network',
    desc: 'Trained community moderators review and verify every report. They are the backbone of SafeHer, keeping the feed accurate, trusted, and free of abuse.',
  },
  {
    icon: '',
    title: 'Share with Authorities',
    desc: 'Escalate a report to local law enforcement with one tap. You stay anonymous while the information reaches the people who can act on it.',
  },
];

const STEPS = [
  { num: '01', title: 'Spot something unsafe', desc: 'Anything from a harassing stranger to a poorly lit street. If it feels unsafe, it\'s worth reporting.' },
  { num: '02', title: 'Report anonymously',    desc: 'Drop a pin, add a description, choose severity. Your identity stays completely hidden.' },
  { num: '03', title: 'Community verifies',    desc: 'Moderators review, neighbours upvote, and verified reports can be forwarded to local authorities.' },
];

export default function LandingPage() {
  return (
    <div className="lp-root">

      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <img src="/logo.svg" alt="SafeHer" className="lp-logo-img" />
          </div>
          <div className="lp-nav-links">
            <a href="#features"    className="lp-nav-link">Features</a>
            <a href="#moderators"  className="lp-nav-link">Moderators</a>
            <a href="#download"    className="lp-nav-link">Download</a>
            <Link to="/about"      className="lp-nav-link">About</Link>
            <Link to="/support"    className="lp-nav-link">Support</Link>
            <Link to="/login" className="lp-nav-btn">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">Community Safety · Powered by You</div>
          <h1 className="lp-hero-h1">
            Safer for Her.<br />
            <span className="lp-hero-accent">Serving all women in public spaces.</span>
          </h1>
          <p className="lp-hero-sub">
            SafeHer is a community-driven safety map where women report incidents
            anonymously, moderators verify them, and local authorities get the
            information they need to act, all without exposing a single identity.
          </p>
          <div className="lp-hero-ctas">
            <a href={PLAY_STORE_URL} className="lp-btn-primary" target="_blank" rel="noopener noreferrer">
              Get it on Google Play
            </a>
            <a href={APP_STORE_URL} className="lp-btn-outline" target="_blank" rel="noopener noreferrer">
              Download on the App Store
            </a>
            <Link to="/tour" className="lp-btn-tour">See how it works →</Link>
            <Link to="/register" className="lp-btn-ghost">Try the web app →</Link>
          </div>
          <div className="lp-trust-bar">
            <span>100% Anonymous</span>
            <span className="lp-trust-dot">·</span>
            <span>No Ads, Ever</span>
            <span className="lp-trust-dot">·</span>
            <span>Community Verified</span>
            <span className="lp-trust-dot">·</span>
            <span>Free Forever</span>
          </div>
        </div>

        {/* Hero image */}
        <div className="lp-hero-visual">
          <img
            src="/hero1.jpeg"
            alt="Women staying safe in public spaces with SafeHer"
            className="lp-hero-img"
          />
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────────── */}
      <section className="lp-section" id="features">
        <div className="lp-section-inner">
          <div className="lp-section-label">What Makes SafeHer Different</div>
          <h2 className="lp-section-h2">Built around your safety and your privacy</h2>
          <div className="lp-features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lp-feature-card">
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────────── */}
      <section className="lp-section lp-how-section">
        <div className="lp-section-inner">
          <div className="lp-section-label">How It Works</div>
          <h2 className="lp-section-h2">Three steps from incident to action</h2>
          <div className="lp-steps-row">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="lp-step">
                  <div className="lp-step-num">{s.num}</div>
                  <h3 className="lp-step-title">{s.title}</h3>
                  <p className="lp-step-desc">{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="lp-step-arrow" aria-hidden="true">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Moderators Are the Pillars ──────────────────────────────────────── */}
      <section className="lp-section lp-mod-section" id="moderators">
        <div className="lp-section-inner lp-mod-inner">
          <div className="lp-mod-text">
            <div className="lp-section-label lp-label-on-dark">Community Pillars</div>
            <h2 className="lp-section-h2 lp-h2-white">
              Moderators are the<br />heart of SafeHer
            </h2>
            <p className="lp-mod-desc">
              SafeHer only works because real people in your community volunteer to keep
              it accurate, respectful, and trustworthy. Moderators review reports, verify
              incidents, message users who need support, and work with authorities when
              serious action is needed. Without them, there is no SafeHer.
            </p>
            <ul className="lp-mod-list">
              <li>Review and verify community incident reports</li>
              <li>Remove false, duplicate, or abusive content</li>
              <li>Privately message reporters who may need help</li>
              <li>Escalate serious incidents to local law enforcement</li>
              <li>Earn trust by covering their local geographic area</li>
            </ul>
            <Link to="/register" className="lp-btn-teal-outline">
              Apply to become a Moderator →
            </Link>
          </div>
          <div className="lp-mod-cards">
            <div className="lp-mod-card">
              <p>Every moderator is vetted by admins and covers a defined geographic area</p>
            </div>
            <div className="lp-mod-card">
              <p>Verified reports earn a trusted badge on the community safety map</p>
            </div>
            <div className="lp-mod-card">
              <p>Private messaging lets moderators follow up with reporters securely</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Download ───────────────────────────────────────────────────────── */}
      <section className="lp-section" id="download">
        <div className="lp-section-inner">
          <div className="lp-section-label">Get the App</div>
          <h2 className="lp-section-h2">Available on Android and iPhone.</h2>
          <div className="lp-download-row">

            {/* Android */}
            <div className="lp-dl-card">
              <h3 className="lp-dl-title">Android</h3>
              <p className="lp-dl-desc">
                Download SafeHer free from the Google Play Store.
              </p>
              <a href={PLAY_STORE_URL} className="lp-btn-primary lp-btn-block" target="_blank" rel="noopener noreferrer">
                Get it on Google Play
              </a>
            </div>

            {/* iOS */}
            <div className="lp-dl-card lp-dl-card-highlight">
              <h3 className="lp-dl-title">iPhone</h3>
              <p className="lp-dl-desc">
                Download SafeHer free from the Apple App Store.
              </p>
              <a href={APP_STORE_URL} className="lp-btn-primary lp-btn-block" target="_blank" rel="noopener noreferrer">
                Download on the App Store
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-logo">
              <img src="/logo192.png" alt="SafeHer" style={{ width: 28, height: 28, objectFit: 'contain' }} />
              <span className="lp-logo-text">SafeHer</span>
            </div>
            <p className="lp-footer-tagline">Your safety, our priority.</p>
          </div>
          <div className="lp-footer-links">
            <Link to="/how-it-works" className="lp-footer-link">How It Works</Link>
            <Link to="/privacy"      className="lp-footer-link">Privacy Policy</Link>
            <Link to="/faq"          className="lp-footer-link">FAQ</Link>
            <Link to="/support"      className="lp-footer-link">Support</Link>
            <Link to="/press"        className="lp-footer-link">Press</Link>
            <Link to="/login"        className="lp-footer-link">Sign In</Link>
            <Link to="/register"     className="lp-footer-link">Register</Link>
            <a href="mailto:support@safeher.xyz" className="lp-footer-link">Contact</a>
          </div>
          <p className="lp-footer-copy">
            © {new Date().getFullYear()} SafeHer · No ads · No data selling · Free forever
          </p>
        </div>
      </footer>

    </div>
  );
}
